import React, { useState } from "react";
import { FaFile } from "react-icons/fa";
import { RiDeleteBin6Line, RiFileWarningFill } from "react-icons/ri";
import { _formatFileSize } from "../../../utils/common";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { FaFileWord } from "react-icons/fa";
import { MdInsertDriveFile } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";
import DefaultButton from "../buttons/defaultButton";

const CommonFilePreview = ({
  icon = false,
  files = [],
  text = "No Files Found!",
  handleClose = () => null,
  close = false,
}) => {
  const [showPreview, setShowPreview] = useState(null);

  const openPreview = (file) => {
    setShowPreview(file);
  };

  const closePreview = () => {
    setShowPreview(null);
  };

  return (
    <div>
      <div className="flex flex-row gap-2 items-center">
        {files && files?.length ? (
          files?.map((file, index) => {
            return (
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openPreview(file);
                }}
                className="h-12 flex flex-row items-center gap-1 text-sm border rounded-md max-w-min cursor-pointer relative overflow-hidden group pl-2"
              >
                {icon ? (
                  <FaFile />
                ) : (
                  <>
                    <FaFile className="text-3xl" />
                    <span>
                      <p className="text-theme truncate max-w-28">
                        {file?.name}
                      </p>
                      <p className="text-textLight">
                        {_formatFileSize(file?.size)}
                      </p>
                    </span>
                  </>
                )}

                <div
                  className={`hidden group-hover:flex justify-center items-center h-full bg-gray-900/20 absolute z-10 left-0 top-0 
                                ${!close ? "w-full" : "w-[calc(100%-3rem)]"}`}
                >
                  <MdOutlineRemoveRedEye className="text-2xl text-white" />
                </div>
                {close && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleClose(file);
                    }}
                    className="bg-gray-100 h-12 w-12 flexCenter text-md"
                  >
                    <RiDeleteBin6Line />
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="flex justify-start gap-1 items-center">
            {!icon ? <RiFileWarningFill className="text-2xl" /> : null}
            {text}
          </div>
        )}
      </div>

      {showPreview ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg max-h-[calc(100%-100px)] container min-h-96 mx-auto overflow-hidden ">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closePreview();
              }}
              className="absolute top-4 right-4 text-theme rounded-full p-2 bg-light text-2xl border"
            >
              <RxCross2 />
            </button>
            <FilePreviewModal file={showPreview} />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const FilePreviewModal = ({ file = null, error = false }) => {
  const name = file?.name || file?.file_name;
  const size = file?.size || file?.file_size;
  const extension = name.split(".").pop().toLowerCase();
  const url = file?.local_url || file?.url;

  const renderPreview = () => {
    if (!file || error) {
      return (
        <div className="rounded-md bg-white flexCenter">No file to preview</div>
      );
    }
    // Handle image files
    if (["jpeg", "jpg", "png", "gif", "bmp", "webp"].includes(extension)) {
      return (
        <img
          src={url}
          alt={name}
          className="h-screen object-contain rounded-md w-full h-full"
        />
      );
    }

    // Handle video files
    if (["mp4", "mov", "mpeg", "mkv", "avi"].includes(extension)) {
      return (
        <video controls className="rounded-md">
          <source src={url} type={`video/${extension}`} />
          Your browser does not support the video tag.
        </video>
      );
    }

    // Handle audio files
    if (["mp3", "wav", "ogg", "m4a"].includes(extension)) {
      return (
        <audio controls className="rounded-md">
          <source src={url} type={`audio/${extension}`} />
          Your browser does not support the audio tag.
        </audio>
      );
    }

    // Handle PDF files
    if (extension === "pdf") {
      return (
        <embed
          src={url}
          type="application/pdf"
          className="w-full h-screen rounded-md"
        />
      );
    }

    // Handle Word files
    if (["doc", "docx"].includes(extension)) {
      return (
        <div
          className="flex items-center justify-center rounded-md cursor-pointer bg-light p-10"
          onClick={() => downloadFile(url, name)}
        >
          <FaFileWord className="w-16 h-16 text-theme" />
          <span className="text-primary-700 font-medium ml-2">
            Word document (Click to download)
          </span>
        </div>
      );
    }

    // Handle unsupported file types
    return (
      <div
        className="flex items-center justify-center rounded-md cursor-pointer bg-light p-10"
        onClick={() => downloadFile(url, name)}
      >
        <MdInsertDriveFile className="w-16 h-16 text-theme" />
        <span className="text-primary-700 font-medium ml-2">
          Unsupported File (Click to download)
        </span>
      </div>
    );
  };

  const downloadFile = (url, fileName) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
  };

  return (
    <div className="flex flex-col gap-2 min-h-60 items-center justify-center rounded-md bg-light">
      {renderPreview()}
      <span className="text-sm text-disabled">{_formatFileSize(size)}</span>
    </div>
  );
};

export default CommonFilePreview;
